import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { distinctUntilChanged, Subscription, take } from 'rxjs';

import { Popup } from '../../../core/shared/components/popup/popup';
import { ToastService } from '../../../core/shared/services/toast.service';
import { CraditService } from '../shared/services/cradit.service';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { RichTextEditor } from '../shared/components/rich-text-editor/rich-text-editor';
import { SectionDragHandle } from '../shared/components/section-drag-handle/section-drag-handle';
import { RichTextPipe } from '../shared/utils/rich-text.pipe';
import { TableCellPipe } from '../shared/utils/table-cell.pipe';
import { richTextForSave } from '../shared/utils/rich-text.util';
import { moveVisibleItem } from '../shared/utils/section-reorder.util';

type BlockType = 'heading' | 'paragraph' | 'table';

interface TableSubHeader {
  id: string;
  title: string;
}

interface TableHeader {
  id: string;
  title: string;
  subHeaders: TableSubHeader[];
}

interface LeafColumn {
  id: string;
  headerId: string;
  subHeaderId: string | null;
  title: string;
}

interface TableRow {
  id: string;
  cells: Record<string, string>;
}

interface BuilderTable {
  headers: TableHeader[];
  leafColumns: LeafColumn[];
  rows: TableRow[];
  hasSubHeaders: boolean;
}

interface BuilderBlock {
  id: string;
  type: BlockType;
  title: string;
  content: string;
  expanded: boolean;
  table?: BuilderTable;
}

interface SavedTable {
  columns: Array<{
    title: string;
    subColumns: string[];
  }>;
  rows: string[][];
}

interface SavedBlock {
  type: BlockType;
  title: string;
  content: string;
  delFlg: number;
  table: SavedTable | null;
}

@Component({
  selector: 'app-institutional-rating-builder',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Popup,
    TranslatePipe,
    RichTextEditor,
    RichTextPipe,
    TableCellPipe,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SectionDragHandle,
  ],
  templateUrl: './institutional-rating-builder.html',
  styleUrl: './institutional-rating-builder.css',
})
export class InstitutionalRatingBuilder
  implements OnInit, OnChanges, OnDestroy {

  @Input() sectionId = '';
  @Input() editable = false;
  @Input() refGrp = 28;
  @Input() refValSeq = 2522;
  @Input() crdtPrpslSeq: number | null = null;

  onboardingId = 0;
  crdtPrpslBlkSeq: number | null = null;

  blocks: BuilderBlock[] = [];

  loading = false;
  saving = false;
  errorMessage = '';
  successMessage = '';

  readonly maxHeaders = 20;
  readonly maxSubHeaders = 20;
  readonly maxRows = 250;

  showDeletePopup = false;
  deleteMessage = '';
  private pendingDeleteBlockId: string | null = null;

  private initialized = false;
  private idCounter = 0;
  private saveTriggerSub?: Subscription;
  private sequenceSub?: Subscription;
  private loadSub?: Subscription;
  private saveApiSub?: Subscription;

  constructor(
    private creditService: CraditService,
    private route: ActivatedRoute,
    private toast: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.onboardingId = this.getOnboardingSequence();

    const existingSequence = this.resolveProposalSequence();
    if (existingSequence > 0) {
      this.crdtPrpslSeq = existingSequence;
    }

    this.sequenceSub = this.creditService.crdtPrpslSeq$
      .pipe(distinctUntilChanged())
      .subscribe((sequence) => {
        const normalized = Number(sequence ?? 0);
        if (!Number.isFinite(normalized) || normalized <= 0) {
          return;
        }

        const changed = normalized !== Number(this.crdtPrpslSeq ?? 0);
        this.crdtPrpslSeq = normalized;

        if (this.initialized && (changed || this.blocks.length === 0)) {
          this.loadSection();
        }
      });

    this.saveTriggerSub = this.creditService.saveSectionTrigger$
      .subscribe((requestedSectionId: string) => {
        if (
          this.normalizeSectionId(requestedSectionId) ===
          this.normalizeSectionId(this.sectionId)
        ) {
          this.saveSection();
        }
      });

    this.initialized = true;

    if (this.resolveProposalSequence() > 0 && Number(this.refValSeq) > 0) {
      this.loadSection();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.initialized) {
      return;
    }

    if (
      changes['sectionId'] ||
      changes['refGrp'] ||
      changes['refValSeq'] ||
      changes['crdtPrpslSeq']
    ) {
      this.blocks = [];
      this.crdtPrpslBlkSeq = null;
      this.loadSection();
    }
  }

  ngOnDestroy(): void {
    this.saveTriggerSub?.unsubscribe();
    this.sequenceSub?.unsubscribe();
    this.loadSub?.unsubscribe();
    this.saveApiSub?.unsubscribe();
  }

  loadSection(): void {
    const proposalSequence = this.resolveProposalSequence();
    const requestedRefValSeq = Number(this.refValSeq);

    if (proposalSequence <= 0 || requestedRefValSeq <= 0) {
      this.blocks = [];
      return;
    }

    this.loadSub?.unsubscribe();
    this.loading = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.loadSub = this.creditService
      .getCrdtBlck(proposalSequence, [requestedRefValSeq])
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          this.loading = false;

          const rows = this.extractResponseRows(response);
          const savedRow =
            rows.find(
              (row: any) =>
                Number(row?.refValSeq) === requestedRefValSeq &&
                Number(row?.delFlg ?? 0) !== 1,
            ) ?? rows[0] ?? null;

          if (!savedRow) {
            this.blocks = [];
            this.crdtPrpslBlkSeq = null;
            return;
          }

          this.crdtPrpslBlkSeq = this.toNullableNumber(
            savedRow?.crdtPrpslBlkSeq,
          );

          this.blocks = this.extractSavedBlocks(savedRow?.jsonData)
            .filter((block: any) => Number(block?.delFlg ?? 0) !== 1)
            .map((block: any) => this.mapSavedBlock(block));
        },
        error: (error: any) => {
          this.loading = false;
          this.blocks = [];
          this.errorMessage =
            error?.error?.message ??
            this.translate.instant('IRS_BUILDER.ERR_LOAD');

          console.error('Institutional Rating load error:', error);
        },
      });
  }

  saveSection(): void {
    if (this.saving) {
      return;
    }

    this.clearMessages();

    const resultSectionId = this.normalizeSectionId(this.sectionId);
    const proposalSequence = this.resolveProposalSequence();

    if (!resultSectionId) {
      this.errorMessage = this.translate.instant('IRS_BUILDER.ERR_SECTION_ID_REQUIRED');
      return;
    }

    if (proposalSequence <= 0) {
      this.reportValidationError(
        resultSectionId,
        this.translate.instant('IRS_BUILDER.ERR_PROPOSAL_SEQ_REQUIRED'),
      );
      return;
    }

    if (this.onboardingId <= 0) {
      this.reportValidationError(
        resultSectionId,
        this.translate.instant('IRS_BUILDER.ERR_ONBOARDING_SEQ_REQUIRED'),
      );
      return;
    }

    if (Number(this.refValSeq) <= 0) {
      this.reportValidationError(
        resultSectionId,
        this.translate.instant('IRS_BUILDER.ERR_REF_VALUE_REQUIRED'),
      );
      return;
    }

    const validationError = this.validateBlocks();
    if (validationError) {
      this.reportValidationError(resultSectionId, validationError);
      return;
    }

    const payload = {
      crprtOnbrdngSeq: this.onboardingId,
      crdtPrpslSeq: proposalSequence,
      blockList: [
        {
          crdtPrpslBlkSeq: this.crdtPrpslBlkSeq,
          refGrp: Number(this.refGrp),
          refValSeq: Number(this.refValSeq),
          blkTyp: 3,
          delFlg: 0,
          sortOrdr: 1,
          txtData: '',
          jsonData: [
            {
              blocks: this.blocks.map((block) => this.toSavedBlock(block)),
            },
          ],
        },
      ],
    };

    console.log('IRS Rating save payload:', payload);

    this.saving = true;
    this.saveApiSub?.unsubscribe();

    this.saveApiSub = this.creditService
      .saveSection(payload)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          this.saving = false;

          const returnedProposalSequence = Number(
            response?.crdtPrpslSeq ??
              response?.data?.crdtPrpslSeq ??
              proposalSequence,
          );

          if (
            Number.isFinite(returnedProposalSequence) &&
            returnedProposalSequence > 0
          ) {
            this.crdtPrpslSeq = returnedProposalSequence;
            this.creditService.setCrdtPrpslSeq(returnedProposalSequence);
          }

          const returnedBlockSequence =
            this.extractReturnedBlockSequence(response);

          if (returnedBlockSequence !== null) {
            this.crdtPrpslBlkSeq = returnedBlockSequence;
          }

          this.successMessage =
            this.translate.instant('IRS_BUILDER.SAVED_SUCCESS');

          this.toast.success(this.successMessage);

          this.creditService.reportSectionSaveResult({
            sectionId: resultSectionId,
            status: 'saved',
          });
        },
        error: (error: any) => {
          this.saving = false;
          this.errorMessage =
            error?.error?.message ??
            this.translate.instant('IRS_BUILDER.ERR_SAVE');

          console.error('Institutional Rating save error:', error);

          this.creditService.reportSectionSaveResult({
            sectionId: resultSectionId,
            status: 'api-error',
            message: this.errorMessage,
          });
        },
      });
  }

  addHeading(): void {
    this.clearMessages();
    this.blocks = [
      ...this.blocks,
      {
        id: this.createId('heading'),
        type: 'heading',
        title: '',
        content: '',
        expanded: true,
      },
    ];
  }

  addParagraph(): void {
    this.clearMessages();
    this.blocks = [
      ...this.blocks,
      {
        id: this.createId('paragraph'),
        type: 'paragraph',
        title: '',
        content: '',
        expanded: true,
      },
    ];
  }

  addTable(): void {
    this.clearMessages();
    this.blocks = [
      ...this.blocks,
      {
        id: this.createId('table'),
        type: 'table',
        title: '',
        content: '',
        expanded: true,
        table: this.createEmptyTable(),
      },
    ];
  }

  requestRemoveBlock(blockId: string): void {
    const block = this.blocks.find((item) => item.id === blockId);
    if (!block) {
      return;
    }

    this.pendingDeleteBlockId = blockId;
    this.deleteMessage = this.translate.instant('IRS_BUILDER.DELETE_CONFIRM', {
      type: this.translate
        .instant('IRS_BUILDER.BLOCK_TYPE.' + String(block.type).toUpperCase())
        .toLowerCase(),
    });
    this.showDeletePopup = true;
  }

  confirmRemoveBlock(): void {
    if (this.pendingDeleteBlockId === null) {
      return;
    }

    const block = this.blocks.find(
      (item) => item.id === this.pendingDeleteBlockId,
    );

    this.removeBlock(this.pendingDeleteBlockId);
    this.pendingDeleteBlockId = null;

    this.toast.success(
      this.translate.instant('IRS_BUILDER.BLOCK_DELETED', {
        type: block
          ? this.translate.instant('IRS_BUILDER.BLOCK_TYPE.' + String(block.type).toUpperCase())
          : this.translate.instant('IRS_BUILDER.BLOCK'),
      }),
    );
  }

  /** Drag-and-drop reorder; blocks are saved in array order. */
  onBlockDrop(event: CdkDragDrop<unknown>): void {
    this.blocks = moveVisibleItem(this.blocks, () => true, event.previousIndex, event.currentIndex);
  }

  removeBlock(blockId: string): void {
    this.blocks = this.blocks.filter((block) => block.id !== blockId);
  }

  toggleBlock(block: BuilderBlock): void {
    block.expanded = !block.expanded;
  }

  addHeader(block: BuilderBlock): void {
    const table = this.ensureTable(block);

    if (table.headers.length >= this.maxHeaders) {
      this.errorMessage =
        this.translate.instant('IRS_BUILDER.ERR_MAX_HEADERS', { max: this.maxHeaders });
      return;
    }

    table.headers = [
      ...table.headers,
      {
        id: this.createId('header'),
        title: '',
        subHeaders: [],
      },
    ];

    this.rebuildTable(table);
  }

  removeHeader(block: BuilderBlock, headerId: string): void {
    const table = this.ensureTable(block);
    table.headers = table.headers.filter((header) => header.id !== headerId);
    this.rebuildTable(table);
  }

  addSubHeader(block: BuilderBlock, headerId: string): void {
    const table = this.ensureTable(block);
    const header = table.headers.find((item) => item.id === headerId);

    if (!header) {
      return;
    }

    if (header.subHeaders.length >= this.maxSubHeaders) {
      this.errorMessage =
        this.translate.instant('IRS_BUILDER.ERR_MAX_SUBHEADERS', { max: this.maxSubHeaders });
      return;
    }

    const firstSubHeader = header.subHeaders.length === 0;
    const newSubHeader: TableSubHeader = {
      id: this.createId('sub-header'),
      title: '',
    };

    header.subHeaders = [...header.subHeaders, newSubHeader];

    const migration = firstSubHeader
      ? { [newSubHeader.id]: this.directLeafId(header.id) }
      : {};

    this.rebuildTable(table, migration);
  }

  removeSubHeader(
    block: BuilderBlock,
    headerId: string,
    subHeaderId: string,
  ): void {
    const table = this.ensureTable(block);
    const header = table.headers.find((item) => item.id === headerId);

    if (!header) {
      return;
    }

    const removingLast = header.subHeaders.length === 1;
    header.subHeaders = header.subHeaders.filter(
      (subHeader) => subHeader.id !== subHeaderId,
    );

    const migration = removingLast
      ? { [this.directLeafId(header.id)]: subHeaderId }
      : {};

    this.rebuildTable(table, migration);
  }

  addRow(block: BuilderBlock): void {
    const table = this.ensureTable(block);

    if (table.leafColumns.length === 0) {
      this.errorMessage =
        this.translate.instant('IRS_BUILDER.ERR_HEADER_BEFORE_ROW');
      return;
    }

    if (table.rows.length >= this.maxRows) {
      this.errorMessage =
        this.translate.instant('IRS_BUILDER.ERR_MAX_ROWS', { max: this.maxRows });
      return;
    }

    const cells: Record<string, string> = {};
    table.leafColumns.forEach((leaf) => {
      cells[leaf.id] = '';
    });

    table.rows = [
      ...table.rows,
      {
        id: this.createId('row'),
        cells,
      },
    ];
  }

  removeRow(block: BuilderBlock, rowId: string): void {
    const table = this.ensureTable(block);
    table.rows = table.rows.filter((row) => row.id !== rowId);
  }

  trackByBlock(_index: number, block: BuilderBlock): string {
    return block.id;
  }

  trackByHeader(_index: number, header: TableHeader): string {
    return header.id;
  }

  trackBySubHeader(_index: number, subHeader: TableSubHeader): string {
    return subHeader.id;
  }

  trackByLeaf(_index: number, leaf: LeafColumn): string {
    return leaf.id;
  }

  trackByRow(_index: number, row: TableRow): string {
    return row.id;
  }

  private createEmptyTable(): BuilderTable {
    return {
      headers: [],
      leafColumns: [],
      rows: [],
      hasSubHeaders: false,
    };
  }

  private ensureTable(block: BuilderBlock): BuilderTable {
    if (!block.table) {
      block.table = this.createEmptyTable();
    }

    return block.table;
  }

  private rebuildTable(
    table: BuilderTable,
    migration: Record<string, string> = {},
  ): void {
    const leafColumns: LeafColumn[] = [];

    table.headers.forEach((header) => {
      if (header.subHeaders.length > 0) {
        header.subHeaders.forEach((subHeader) => {
          leafColumns.push({
            id: subHeader.id,
            headerId: header.id,
            subHeaderId: subHeader.id,
            title: subHeader.title,
          });
        });
        return;
      }

      leafColumns.push({
        id: this.directLeafId(header.id),
        headerId: header.id,
        subHeaderId: null,
        title: header.title,
      });
    });

    table.rows = table.rows.map((row) => {
      const cells: Record<string, string> = {};

      leafColumns.forEach((leaf) => {
        const oldId = migration[leaf.id];
        cells[leaf.id] = String(
          row.cells[leaf.id] ?? (oldId ? row.cells[oldId] : '') ?? '',
        );
      });

      return { ...row, cells };
    });

    table.leafColumns = leafColumns;
    table.hasSubHeaders = table.headers.some(
      (header) => header.subHeaders.length > 0,
    );
  }

  private mapSavedBlock(source: any): BuilderBlock {
    const type = this.normalizeBlockType(source?.type);

    return {
      id: this.createId('block'),
      type,
      title: String(source?.title ?? ''),
      content: String(source?.content ?? ''),
      expanded: false,
      table:
        type === 'table'
          ? this.fromSavedTable(source?.table)
          : undefined,
    };
  }

  private fromSavedTable(source: any): BuilderTable {
    const rawColumns = Array.isArray(source?.columns)
      ? source.columns
      : [];

    const headers: TableHeader[] = rawColumns.map((column: any) => ({
      id: this.createId('header'),
      title:
        typeof column === 'string'
          ? column
          : String(column?.title ?? ''),
      subHeaders: Array.isArray(column?.subColumns)
        ? column.subColumns.map((title: unknown) => ({
            id: this.createId('sub-header'),
            title: String(title ?? ''),
          }))
        : [],
    }));

    const table: BuilderTable = {
      headers,
      leafColumns: [],
      rows: [],
      hasSubHeaders: false,
    };

    this.rebuildTable(table);

    const rawRows = Array.isArray(source?.rows) ? source.rows : [];
    table.rows = rawRows.map((row: unknown) => {
      const values = Array.isArray(row) ? row : [];
      const cells: Record<string, string> = {};

      table.leafColumns.forEach((leaf, index) => {
        cells[leaf.id] = String(values[index] ?? '');
      });

      return {
        id: this.createId('row'),
        cells,
      };
    });

    return table;
  }

  private toSavedBlock(block: BuilderBlock): SavedBlock {
    return {
      type: block.type,
      title: String(block.title ?? '').trim(),
      content:
        block.type === 'paragraph' ? richTextForSave(block.content) : String(block.content ?? ''),
      delFlg: 0,
      table:
        block.type === 'table' && block.table
          ? this.toSavedTable(block.table)
          : null,
    };
  }

  private toSavedTable(table: BuilderTable): SavedTable {
    return {
      columns: table.headers.map((header) => ({
        title: String(header.title ?? '').trim(),
        subColumns: header.subHeaders.map((subHeader) =>
          String(subHeader.title ?? '').trim(),
        ),
      })),
      rows: table.rows.map((row) =>
        table.leafColumns.map((leaf) =>
          String(row.cells[leaf.id] ?? ''),
        ),
      ),
    };
  }

  private validateBlocks(): string {
    for (let blockIndex = 0; blockIndex < this.blocks.length; blockIndex++) {
      const block = this.blocks[blockIndex];

      if (block.type === 'heading' && !block.title.trim()) {
        block.expanded = true;
        return this.translate.instant('IRS_BUILDER.ERR_HEADING_TITLE', { n: blockIndex + 1 });
      }

      if (block.type === 'paragraph' && !block.content.trim()) {
        block.expanded = true;
        return this.translate.instant('IRS_BUILDER.ERR_PARAGRAPH_CONTENT', { n: blockIndex + 1 });
      }

      if (block.type !== 'table') {
        continue;
      }

      const table = this.ensureTable(block);

      if (table.headers.length === 0) {
        block.expanded = true;
        return this.translate.instant('IRS_BUILDER.ERR_TABLE_HEADER', { n: blockIndex + 1 });
      }

      for (let headerIndex = 0; headerIndex < table.headers.length; headerIndex++) {
        const header = table.headers[headerIndex];

        if (!header.title.trim()) {
          block.expanded = true;
          return this.translate.instant('IRS_BUILDER.ERR_MAIN_HEADER_TITLE', {
            n: blockIndex + 1,
            header: headerIndex + 1,
          });
        }

        for (
          let subHeaderIndex = 0;
          subHeaderIndex < header.subHeaders.length;
          subHeaderIndex++
        ) {
          if (!header.subHeaders[subHeaderIndex].title.trim()) {
            block.expanded = true;
            return this.translate.instant('IRS_BUILDER.ERR_SUBHEADER_TITLE', {
              n: blockIndex + 1,
              header: headerIndex + 1,
              sub: subHeaderIndex + 1,
            });
          }
        }
      }
    }

    return '';
  }

  private reportValidationError(sectionId: string, message: string): void {
    this.errorMessage = message;
    this.creditService.reportSectionSaveResult({
      sectionId,
      status: 'validation-error',
      message,
    });
  }

  private extractReturnedBlockSequence(response: any): number | null {
    const possibleValues = [
      response?.crdtPrpslBlkSeq,
      response?.data?.crdtPrpslBlkSeq,
      response?.blockList?.[0]?.crdtPrpslBlkSeq,
      response?.data?.blockList?.[0]?.crdtPrpslBlkSeq,
      response?.crdtPrpslBlkList?.[0]?.crdtPrpslBlkSeq,
      response?.data?.crdtPrpslBlkList?.[0]?.crdtPrpslBlkSeq,
    ];

    for (const value of possibleValues) {
      const sequence = this.toNullableNumber(value);
      if (sequence !== null) {
        return sequence;
      }
    }

    return null;
  }

  private extractResponseRows(response: any): any[] {
    const possibleLists = [
      response,
      response?.data,
      response?.content,
      response?.data?.content,
      response?.blockList,
      response?.data?.blockList,
      response?.crdtPrpslBlkList,
      response?.data?.crdtPrpslBlkList,
    ];

    const list = possibleLists.find((value) => Array.isArray(value));
    return Array.isArray(list) ? list : [];
  }

  private extractSavedBlocks(jsonData: unknown): any[] {
    const normalized = this.normalizeJsonData(jsonData);
    return Array.isArray(normalized[0]?.blocks)
      ? normalized[0].blocks
      : [];
  }

  private normalizeJsonData(value: unknown): Array<{ blocks?: any[] }> {
    if (value === null || value === undefined || value === '') {
      return [];
    }

    if (Array.isArray(value)) {
      return value as Array<{ blocks?: any[] }>;
    }

    if (typeof value === 'object') {
      return [value as { blocks?: any[] }];
    }

    if (typeof value === 'string') {
      try {
        return this.normalizeJsonData(JSON.parse(value));
      } catch (error) {
        console.warn('Invalid IRS jsonData:', value, error);
      }
    }

    return [];
  }

  private normalizeBlockType(value: unknown): BlockType {
    const type = String(value ?? '').trim().toLowerCase();
    return type === 'heading' || type === 'paragraph' || type === 'table'
      ? type
      : 'paragraph';
  }

  private resolveProposalSequence(): number {
    return Number(
      this.crdtPrpslSeq ??
        this.creditService.getCrdtPrpslSeq() ??
        0,
    );
  }

  private directLeafId(headerId: string): string {
    return `direct:${headerId}`;
  }

  private createId(prefix: string): string {
    this.idCounter += 1;
    return `${prefix}-${Date.now()}-${this.idCounter}`;
  }

  private clearMessages(): void {
    this.errorMessage = '';
    this.successMessage = '';
  }

  private capitalize(value: string): string {
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  private normalizeSectionId(value: unknown): string {
    return String(value ?? '').trim();
  }

  private getOnboardingSequence(): number {
    let currentRoute: ActivatedRoute | null = this.route;

    while (currentRoute) {
      const value =
        currentRoute.snapshot.paramMap.get('id') ??
        currentRoute.snapshot.paramMap.get('oid');

      const sequence = Number(value);

      if (value && Number.isFinite(sequence) && sequence > 0) {
        return sequence;
      }

      currentRoute = currentRoute.parent;
    }

    return 0;
  }

  private toNullableNumber(value: unknown): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numberValue = Number(value);
    return Number.isFinite(numberValue) ? numberValue : null;
  }
}